import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { Workflow, Play, Save, Trash2, ArrowDown, Bot, Database, Mail, Settings, Loader2, ChevronUp, ChevronDown, Pencil, X, BookOpen, Zap } from 'lucide-react'
import ProductGuide from '../components/ProductGuide'
import { getSession } from '../lib/session'

// Node types the builder offers. The engine runs each as an LLM step with the
// step's system prompt, chained in order (each step's output feeds the next).
const NODE_TYPES = [
  { type: 'llm', label: 'LLM Agent', icon: Bot, color: 'bg-primary-500', hint: 'Reason over the input and produce a result' },
  { type: 'rag', label: 'RAG Query', icon: Database, color: 'bg-purple-500', hint: 'Retrieve + answer from your knowledge base' },
  { type: 'email', label: 'Send Email', icon: Mail, color: 'bg-green-500', hint: 'Draft AND actually send an email' },
  { type: 'transform', label: 'Transform', icon: Settings, color: 'bg-orange-500', hint: 'Reshape / clean / format the input' },
]
const defOf = (t) => NODE_TYPES.find(n => n.type === t) || NODE_TYPES[0]
// Model TIERS (keys) — same catalog as the rest of the platform. Each step's
// run meters credits = the tier's weight. Empty = the workspace default (1×).
const MODELS = [
  { id: '', label: 'Workspace default (1 credit)' },
  { id: 'economy', label: 'Economy · Kimi K2 (1 credit)' },
  { id: 'standard', label: 'Standard · GPT-4o mini (3 credits)' },
  { id: 'premium', label: 'Premium · GPT-4o (10 credits)' },
  { id: 'frontier', label: 'Frontier · Claude Sonnet (20 credits)' },
]

let SEQ = 0
const newId = () => `node_${Date.now()}_${SEQ++}`

export default function WorkflowBuilder() {
  const [nodes, setNodes] = useState([])        // ordered pipeline: [{id,type,label,systemPrompt,model}]
  const [selectedId, setSelectedId] = useState(null)
  const [workflowName, setWorkflowName] = useState('')
  const [editingId, setEditingId] = useState(null) // set when editing a saved workflow
  const [saving, setSaving] = useState(false)
  const [running, setRunning] = useState(false)
  const [run, setRun] = useState(null)          // { output, log } from a canvas run

  const selected = nodes.find(n => n.id === selectedId) || null
  const session = getSession()

  // Instances (for the "pin to a knowledge base" picker) + credit meter.
  const [instances, setInstances] = useState([])
  const [credits, setCredits] = useState(null)
  const loadCredits = () => { if (session?.namespaceId) axios.get(`/api/v1/tenants/${session.namespaceId}/credits`).then(r => setCredits(r.data)).catch(() => {}) }
  useEffect(() => {
    if (!session?.namespaceId) return
    axios.get(`/api/v1/tenants/${session.namespaceId}/instances`).then(r => setInstances(r.data.instances || [])).catch(() => {})
    loadCredits()
  }, [session?.namespaceId])

  // ── Saved workflows list (run / load / delete) ─────────────────────────────
  const [saved, setSaved] = useState([])
  const [runState, setRunState] = useState({})
  const loadSaved = () => axios.get('/api/v1/orchestration/workflows').then(r => setSaved(r.data.workflows || [])).catch(() => {})
  useEffect(() => { loadSaved() }, [])
  const setRS = (id, patch) => setRunState(s => ({ ...s, [id]: { ...s[id], ...patch } }))
  const runOne = async (id) => {
    const input = runState[id]?.input || 'Test message'
    setRS(id, { busy: true, output: null, error: null })
    try {
      const r = await axios.post(`/api/v1/orchestration/workflows/${id}/run`, { input: { message: input } })
      setRS(id, { busy: false, output: typeof r.data.output === 'string' ? r.data.output : JSON.stringify(r.data.output, null, 2) })
    } catch (err) { setRS(id, { busy: false, error: err.response?.data?.error || 'Run failed' }) }
  }

  // ── Build the pipeline ─────────────────────────────────────────────────────
  const addNode = (type) => {
    const d = defOf(type)
    const node = { id: newId(), type, label: d.label, systemPrompt: '', model: '', useKnowledge: type === 'rag', instanceId: '', recipient: '' }
    setNodes(ns => [...ns, node])
    setSelectedId(node.id)
  }
  const updateNode = (id, patch) => setNodes(ns => ns.map(n => n.id === id ? { ...n, ...patch } : n))
  const removeNode = (id) => { setNodes(ns => ns.filter(n => n.id !== id)); if (selectedId === id) setSelectedId(null) }
  const moveNode = (id, dir) => setNodes(ns => {
    const i = ns.findIndex(n => n.id === id); const j = i + dir
    if (i < 0 || j < 0 || j >= ns.length) return ns
    const copy = [...ns];[copy[i], copy[j]] = [copy[j], copy[i]]; return copy
  })
  const resetCanvas = () => { setNodes([]); setSelectedId(null); setWorkflowName(''); setEditingId(null); setRun(null) }

  // A definition the engine actually runs: each step depends on the previous one,
  // carrying its own type / prompt / model. (edges are just for drawing.)
  const buildDefinition = () => ({
    nodes: nodes.map((n, i) => ({
      id: n.id, type: n.type, label: n.label,
      systemPrompt: n.systemPrompt, model: n.model,
      useKnowledge: !!n.useKnowledge, instanceId: n.instanceId || null,
      recipient: n.recipient || null,
      dependencies: i === 0 ? [] : [nodes[i - 1].id],
    })),
    edges: nodes.slice(1).map((n, i) => ({ from: nodes[i].id, to: n.id })),
  })

  const save = async () => {
    if (!workflowName.trim()) { toast.error('Name your workflow first'); return null }
    if (!nodes.length) { toast.error('Add at least one step'); return null }
    setSaving(true)
    try {
      const body = { name: workflowName.trim(), definition: buildDefinition() }
      const r = editingId
        ? await axios.put(`/api/v1/orchestration/workflows/${editingId}`, body)
        : await axios.post('/api/v1/orchestration/workflows', body)
      const wf = r.data.workflow
      setEditingId(wf.id)
      toast.success(editingId ? 'Workflow updated' : 'Workflow saved')
      loadSaved()
      return wf
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed'); return null
    } finally { setSaving(false) }
  }

  const saveAndRun = async () => {
    const wf = await save()
    if (!wf) return
    setRunning(true); setRun(null)
    try {
      const r = await axios.post(`/api/v1/orchestration/workflows/${wf.id}/run`, { input: { message: 'Test run from builder' } })
      setRun({ output: typeof r.data.output === 'string' ? r.data.output : JSON.stringify(r.data.output, null, 2), log: r.data.log || [], credits: r.data.credits, creditsRemaining: r.data.creditsRemaining })
      toast.success('Workflow ran')
      loadCredits()
    } catch (err) {
      setRun({ error: err.response?.data?.error || 'Run failed' })
      if (err.response?.data?.code === 'CREDIT_LIMIT_REACHED') loadCredits()
    } finally { setRunning(false) }
  }

  const loadIntoCanvas = async (id) => {
    try {
      const r = await axios.get(`/api/v1/orchestration/workflows/${id}`)
      const wf = r.data.workflow
      const def = wf.definition || {}
      const list = Array.isArray(def.nodes) ? def.nodes
        : def.nodes ? Object.entries(def.nodes).map(([k, v]) => ({ id: k, ...v }))
        : Array.isArray(def.steps) ? def.steps : []
      setNodes(list.map(n => ({
        id: n.id || newId(), type: n.type || 'llm', label: n.label || defOf(n.type).label,
        systemPrompt: n.systemPrompt || n.prompt || '', model: n.model || '',
        useKnowledge: n.useKnowledge ?? (n.type === 'rag'), instanceId: n.instanceId || '',
        recipient: n.recipient || '',
      })))
      setWorkflowName(wf.name); setEditingId(wf.id); setSelectedId(null); setRun(null)
      toast.success(`Loaded "${wf.name}" — edit and re-run`)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch { toast.error('Could not load workflow') }
  }

  const deleteSaved = async (id, name) => {
    if (!window.confirm(`Delete workflow "${name}"? This can't be undone.`)) return
    try { await axios.delete(`/api/v1/orchestration/workflows/${id}`); toast.success('Deleted'); if (editingId === id) resetCanvas(); loadSaved() }
    catch { toast.error('Delete failed') }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Agentic Orchestration</h2>
          <p className="text-gray-500 mt-1">Chain AI steps into a pipeline — each step’s output feeds the next.</p>
        </div>
        <div className="flex gap-2 items-center">
          {credits && (
            <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-gray-200 bg-white text-gray-600" title="Each step spends credits by model tier">
              <Zap size={13} className="text-amber-500" />
              {credits.unlimited ? 'Unlimited credits' : `${credits.remaining} / ${credits.limit} credits left`}
            </span>
          )}
          <button onClick={save} disabled={saving} className="btn-secondary flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {editingId ? 'Update' : 'Save'}
          </button>
          <button onClick={saveAndRun} disabled={running || saving} className="btn-primary flex items-center gap-2 disabled:opacity-60">
            {running ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />} {running ? 'Running…' : 'Save & Run'}
          </button>
        </div>
      </div>

      <ProductGuide product="orchestration" collapsible defaultOpen={false} />

      {/* Saved workflows — run / edit / delete */}
      {saved.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Your workflows <span className="text-sm font-normal text-gray-400">({saved.length})</span></h3>
          <div className="space-y-2">
            {saved.map(w => {
              const rs = runState[w.id] || {}
              return (
                <div key={w.id} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{w.name}</p>
                      <p className="text-xs text-gray-400">v{w.version} · {w.status} · created {new Date(w.created_at).toLocaleDateString()}</p>
                    </div>
                    <input className="input py-1.5 text-sm w-44" placeholder="Input message…" value={rs.input || ''} onChange={e => setRS(w.id, { input: e.target.value })} />
                    <button onClick={() => runOne(w.id)} disabled={rs.busy} className="btn-primary text-sm px-4 py-1.5 flex items-center gap-1.5 disabled:opacity-60">
                      {rs.busy ? <Loader2 className="animate-spin" size={14} /> : <Play size={14} />} Run
                    </button>
                    <button onClick={() => loadIntoCanvas(w.id)} className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 flex items-center gap-1.5"><Pencil size={13} /> Edit</button>
                    <button onClick={() => deleteSaved(w.id, w.name)} className="text-sm px-2 py-1.5 rounded-lg border border-gray-200 text-gray-400 hover:text-red-600 hover:border-red-200"><Trash2 size={14} /></button>
                  </div>
                  {rs.output && <pre className="mt-2 text-xs bg-gray-50 border border-gray-100 rounded p-2 whitespace-pre-wrap max-h-40 overflow-y-auto">{rs.output}</pre>}
                  {rs.error && <p className="mt-2 text-xs text-red-600">{rs.error}</p>}
                </div>
              )
            })}
          </div>
        </div>
      )}

      <div className="flex gap-6 items-start">
        {/* Palette + properties */}
        <div className="w-64 space-y-3 shrink-0">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Add a step</h3>
          {NODE_TYPES.map(nt => (
            <button key={nt.type} onClick={() => addNode(nt.type)}
              className="w-full flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-sm transition-all text-left">
              <div className={`${nt.color} text-white p-2 rounded-md shrink-0`}><nt.icon size={16} /></div>
              <div className="min-w-0">
                <span className="text-sm font-medium text-gray-900 block">{nt.label}</span>
                <span className="text-[11px] text-gray-400 leading-tight block">{nt.hint}</span>
              </div>
            </button>
          ))}

          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mt-6">Step settings</h3>
          {selected ? (
            <div className="card p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-900">{selected.label}</p>
                <button onClick={() => setSelectedId(null)} className="text-gray-300 hover:text-gray-600"><X size={15} /></button>
              </div>
              <div>
                <label className="text-xs text-gray-600">Name</label>
                <input className="input text-sm mt-1" value={selected.label} onChange={e => updateNode(selected.id, { label: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-gray-600">Instructions (system prompt)</label>
                <textarea className="input text-xs h-28 mt-1" placeholder="What should this step do with the input?"
                  value={selected.systemPrompt} onChange={e => updateNode(selected.id, { systemPrompt: e.target.value })} />
                <p className="text-[11px] text-gray-400 mt-1">Left blank, this step uses a sensible default for a {selected.label}.</p>
              </div>
              <div>
                <label className="text-xs text-gray-600">Model</label>
                <select className="input text-sm mt-1" value={selected.model} onChange={e => updateNode(selected.id, { model: e.target.value })}>
                  {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </div>

              {/* Knowledge grounding */}
              <div className="pt-1 border-t border-gray-100">
                <label className="flex items-center gap-2 cursor-pointer mt-2">
                  <input type="checkbox" checked={!!selected.useKnowledge} onChange={e => updateNode(selected.id, { useKnowledge: e.target.checked })} className="rounded border-gray-300" />
                  <span className="text-sm text-gray-800 flex items-center gap-1.5"><BookOpen size={14} className="text-primary-500" /> Use my knowledge base</span>
                </label>
                {selected.useKnowledge && (
                  <div className="mt-2">
                    <label className="text-xs text-gray-600">Search which knowledge?</label>
                    <select className="input text-sm mt-1" value={selected.instanceId} onChange={e => updateNode(selected.id, { instanceId: e.target.value })}>
                      <option value="">Whole workspace (all instances)</option>
                      {instances.map(i => <option key={i.id} value={i.id}>{i.name}{i.isDefault ? ' (default)' : ''}</option>)}
                    </select>
                    <p className="text-[11px] text-gray-400 mt-1">This step retrieves the most relevant passages and answers from them.</p>
                  </div>
                )}
              </div>

              {/* Send Email recipient */}
              {selected.type === 'email' && (
                <div className="pt-1 border-t border-gray-100">
                  <label className="text-xs text-gray-600 flex items-center gap-1.5 mt-2"><Mail size={13} className="text-green-500" /> Send to</label>
                  <input className="input text-sm mt-1" type="email" placeholder="ops@yourcompany.com"
                    value={selected.recipient} onChange={e => updateNode(selected.id, { recipient: e.target.value })} />
                  <p className="text-[11px] text-gray-400 mt-1">This step really sends. Leave blank to email the workspace owner.</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-400">Click a step in the pipeline to configure it.</p>
          )}
        </div>

        {/* Pipeline canvas */}
        <div className="flex-1 card min-h-[420px] bg-gray-50/60">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <input type="text" className="input w-64 text-sm" placeholder="Workflow name…" value={workflowName} onChange={e => setWorkflowName(e.target.value)} />
            {editingId && <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Editing saved workflow</span>}
            {nodes.length > 0 && <button onClick={resetCanvas} className="text-xs text-gray-400 hover:text-gray-700 ml-auto">Clear / new</button>}
          </div>

          {nodes.length === 0 ? (
            <div className="h-72 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <Workflow size={44} className="mx-auto mb-3 opacity-50" />
                <p className="font-medium text-gray-500">Build a pipeline</p>
                <p className="text-sm">Add steps from the left. They run top-to-bottom, each feeding the next.</p>
              </div>
            </div>
          ) : (
            <div className="max-w-xl mx-auto">
              <div className="text-center text-[11px] uppercase tracking-wide text-gray-400 mb-2">Input message</div>
              {nodes.map((n, i) => {
                const d = defOf(n.type)
                return (
                  <div key={n.id}>
                    <div className="flex justify-center"><ArrowDown size={18} className="text-gray-300" /></div>
                    <div onClick={() => setSelectedId(n.id)}
                      className={`relative bg-white rounded-xl border p-3 flex items-start gap-3 cursor-pointer transition-all ${selectedId === n.id ? 'border-primary-400 ring-2 ring-primary-100' : 'border-gray-200 hover:border-gray-300'}`}>
                      <div className={`${d.color} text-white w-8 h-8 rounded-lg flex items-center justify-center shrink-0`}><d.icon size={16} /></div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] text-gray-400 tabular-nums">Step {i + 1}</span>
                          <span className="text-sm font-medium text-gray-900 truncate">{n.label}</span>
                          <span className="text-[10px] text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">{(MODELS.find(m => m.id === n.model)?.label || 'Workspace default').split(' · ')[0]}</span>
                          {n.useKnowledge && <span className="text-[10px] text-primary-600 bg-primary-50 rounded px-1.5 py-0.5 flex items-center gap-1"><BookOpen size={10} /> {n.instanceId ? (instances.find(i => i.id === n.instanceId)?.name || 'KB') : 'Workspace KB'}</span>}
                          {n.type === 'email' && <span className="text-[10px] text-green-600 bg-green-50 rounded px-1.5 py-0.5 flex items-center gap-1"><Mail size={10} /> {n.recipient || 'owner'}</span>}
                        </div>
                        <p className="text-xs text-gray-500 truncate mt-0.5">{n.systemPrompt || <span className="italic text-gray-400">Default {n.label} behavior — click to customize</span>}</p>
                      </div>
                      <div className="flex flex-col items-center gap-0.5 shrink-0" onClick={e => e.stopPropagation()}>
                        <button onClick={() => moveNode(n.id, -1)} disabled={i === 0} className="text-gray-300 hover:text-gray-700 disabled:opacity-30"><ChevronUp size={15} /></button>
                        <button onClick={() => moveNode(n.id, 1)} disabled={i === nodes.length - 1} className="text-gray-300 hover:text-gray-700 disabled:opacity-30"><ChevronDown size={15} /></button>
                      </div>
                      <button onClick={e => { e.stopPropagation(); removeNode(n.id) }} className="text-gray-300 hover:text-red-600 shrink-0"><Trash2 size={15} /></button>
                    </div>
                  </div>
                )
              })}
              <div className="flex justify-center"><ArrowDown size={18} className="text-gray-300" /></div>
              <div className="text-center text-[11px] uppercase tracking-wide text-gray-400">Final output</div>
            </div>
          )}

          {/* Canvas run result */}
          {(run || running) && (
            <div className="mt-5 border-t border-gray-200 pt-4 max-w-xl mx-auto">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-gray-800">Run result</p>
                {typeof run?.credits === 'number' && (
                  <span className="text-xs text-gray-500 flex items-center gap-1"><Zap size={12} className="text-amber-500" /> {run.credits} credit{run.credits === 1 ? '' : 's'} used{run.creditsRemaining != null ? ` · ${run.creditsRemaining} left` : ''}</span>
                )}
              </div>
              {running && <p className="text-sm text-gray-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Running the pipeline…</p>}
              {run?.error && <p className="text-sm text-red-600">{run.error}</p>}
              {run?.log?.length > 0 && (
                <div className="space-y-1.5 mb-3">
                  {run.log.map((l, i) => (
                    <details key={i} className="text-xs bg-gray-50 border border-gray-100 rounded p-2">
                      <summary className="cursor-pointer text-gray-600">Step {i + 1}{l.label ? ` · ${l.label}` : ''} · {l.duration}ms{typeof l.credits === 'number' ? ` · ${l.credits} cr` : ''}{l.grounded ? ` · ${l.passages} passages` : ''}{l.emailed ? ` · ✉ sent to ${l.recipient}` : ''}</summary>
                      <pre className="whitespace-pre-wrap mt-1 text-gray-700">{typeof l.output === 'string' ? l.output : JSON.stringify(l.output, null, 2)}</pre>
                    </details>
                  ))}
                </div>
              )}
              {run?.output && <pre className="text-xs bg-white border border-gray-200 rounded p-3 whitespace-pre-wrap max-h-56 overflow-y-auto">{run.output}</pre>}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
